import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import api from '../lib/api';
import { multipartUpload } from '../lib/multipartUpload';
import UploadSheet from '../ui/UploadSheet';

// The global upload queue (DESIGN rev 2.1 §2.6: one Upload sheet, per-file progress, continues in the background).
// The queue lives at module level, not in React state, so an upload keeps running while the user moves between
// pages and between shells (/review mounts its own RailShell). UploadProvider only renders the sheet.
//
// Video uploads go through lib/multipartUpload.js against /api/video-assets/multipart/* (start, sign-part,
// complete, abort; the part size comes from the start response), then POST /api/video-assets/confirm, the same
// confirm call the old VideoUploader made after its single presigned PUT.
// Any other multipart route group (Storage, Raw footage) queues through enqueueUpload with its own endpoints, start
// body and confirm step (HD-6), so every upload in the app shows in the same sheet.

const VIDEO_ENDPOINTS = {
  start:    '/api/video-assets/multipart/start',
  signPart: '/api/video-assets/multipart/sign-part',
  complete: '/api/video-assets/multipart/complete',
  abort:    '/api/video-assets/multipart/abort',
};

let items = [];                 // [{ id, name, size, progress 0..1, status, error?, meta, result? }]
const files = new Map();        // id → File (kept for Retry)
const assetIds = new Map();     // id → upload id (videoAssetId, footageId…) once S3 has the object (Retry then only re-confirms)
const handles = new Map();      // id → the running multipartUpload ({ cancel }) while S3 parts are in flight
const cancelled = new Set();    // ids the user cancelled: their rejection drops the row instead of marking it failed
const jobs = new Map();         // id → { endpoints, startBody, idFieldName, confirm, pool, limit } for enqueueUpload items

// enqueueUpload items run at most `limit` at a time per pool (meta.kind): the old Raw footage uploader ran 3 in
// parallel, and a camera card of 30 clips must not open 30 multipart uploads at once.
function pump(pool) {
  const mine = items.filter(i => jobs.get(i.id)?.pool === pool);
  let free = (jobs.get(mine[0]?.id)?.limit || 3) - mine.filter(i => i.status === 'uploading').length;
  for (const i of mine) { if (free <= 0) break; if (i.status === 'queued') { free--; run(i.id); } }
}
const itemListeners = new Set();
const doneListeners = new Set();
let seq = 0;

const emit = () => itemListeners.forEach(l => l());
const patch = (id, next) => { items = items.map(i => (i.id === id ? { ...i, ...next } : i)); emit(); };

async function run(id) {
  const job = jobs.get(id);
  if (!job) return runVideo(id);
  const item = items.find(i => i.id === id);
  const file = files.get(id);
  if (!item || !file) return;
  patch(id, { status: 'uploading', error: undefined, progress: assetIds.has(id) ? 1 : 0 });
  try {
    let uploadedId = assetIds.get(id);
    if (!uploadedId) {
      const onProgress = (loaded, total) => patch(id, { progress: total ? loaded / total : 0 });
      const up = job.transfer
        ? job.transfer(file, onProgress)
        : multipartUpload({ file, api, endpoints: job.endpoints, startBody: job.startBody, idFieldName: job.idFieldName, onProgress });
      if (up.cancel) handles.set(id, up);
      const res = await up.promise.finally(() => handles.delete(id));
      uploadedId = job.transfer ? res : res[job.idFieldName];
      assetIds.set(id, uploadedId);
    }
    const result = job.confirm ? await job.confirm(uploadedId, file) : { id: uploadedId };
    patch(id, { status: 'done', progress: 1, result });
    files.delete(id); assetIds.delete(id); jobs.delete(id);
    const done = items.find(i => i.id === id);
    doneListeners.forEach(l => l(done));
  } catch (err) {
    if (cancelled.delete(id)) dismissUpload(id);
    else patch(id, { status: 'failed', error: err.response?.data?.error || err.message || 'The upload stopped. Retry it.' });
  }
  pump(job.pool);
}

async function runVideo(id) {
  const item = items.find(i => i.id === id);
  const file = files.get(id);
  if (!item || !file) return;
  patch(id, { status: 'uploading', error: undefined, progress: assetIds.has(id) ? 1 : 0 });
  const { clientId, liveProductionId, aiProductionId } = item.meta;
  try {
    let videoAssetId = assetIds.get(id);
    if (!videoAssetId) {
      const up = multipartUpload({
        file,
        api,
        endpoints: VIDEO_ENDPOINTS,
        startBody: {
          clientId,
          ...(liveProductionId ? { liveProductionId } : { aiProductionId }),
          contentType: file.type || 'video/mp4',
        },
        idFieldName: 'videoAssetId',
        onProgress: (loaded, total) => patch(id, { progress: total ? loaded / total : 0 }),
      });
      handles.set(id, up);
      ({ videoAssetId } = await up.promise.finally(() => handles.delete(id)));
      assetIds.set(id, videoAssetId);
    }
    const res = await api.post('/api/video-assets/confirm', { videoAssetId });
    patch(id, { status: 'done', progress: 1, result: res.data.videoAsset });
    files.delete(id);
    assetIds.delete(id);
    const done = items.find(i => i.id === id);
    doneListeners.forEach(l => l(done));
  } catch (err) {
    if (cancelled.delete(id)) dismissUpload(id);
    else patch(id, { status: 'failed', error: err.response?.data?.error || err.message || 'The upload stopped. Retry it.' });
  }
}

/** Queue a video for a production card. meta.productionId is the card id (Live or AI), for pages that watch it. */
export function enqueueVideoUpload({ file, clientId, liveProductionId, aiProductionId }) {
  const id = `up-${Date.now()}-${++seq}`;
  files.set(id, file);
  items = [...items, {
    id, name: file.name, size: file.size, progress: 0, status: 'queued',
    meta: { kind: 'video', clientId, liveProductionId, aiProductionId, productionId: liveProductionId || aiProductionId },
  }];
  emit();
  runVideo(id);
  return id;
}

/**
 * Queue a file for any multipart route group. endpoints: { start, signPart, complete, abort }; startBody: extra fields
 * for <start>; idFieldName: the id key in the start response; confirm(uploadedId, file) → result (optional; runs after
 * S3 has the object and is the only step Retry repeats once it has). meta.kind names the source so pages can filter
 * useUploadDone events (e.g. { kind: 'storage', folderId }) and is the concurrency pool: at most `limit` (default 3)
 * files of one kind upload at a time; the rest wait as "queued".
 * transfer(file, onProgress(loaded, total)) → { promise → uploaded id, cancel? }: for a route that is not multipart (the
 * Asset library's presigned PUT, its ZIP post, HD-10); it replaces endpoints / startBody / idFieldName. Without cancel
 * the sheet offers no Cancel while the file is in flight.
 */
export function enqueueUpload({ file, endpoints, startBody = {}, idFieldName, confirm, meta = {}, limit = 3, transfer }) {
  const id = `up-${Date.now()}-${++seq}`;
  const pool = meta.kind || 'file';
  files.set(id, file);
  jobs.set(id, { endpoints, startBody, idFieldName, confirm, pool, limit, transfer });
  items = [...items, { id, name: file.name, size: file.size, progress: 0, status: 'queued', meta }];
  emit();
  pump(pool);
  return id;
}

export function retryUpload(id) {
  const job = jobs.get(id);
  if (!job) return runVideo(id);
  patch(id, { status: 'queued', error: undefined });
  pump(job.pool);
}
/**
 * Cancel a queued or uploading file. A queued file leaves the list; an uploading one stops its parts, multipartUpload
 * posts the route's abort (S3 discards the parts, the server cleans its row), and then it leaves the list. Once S3
 * has the object (only the confirm step remains) there is nothing to cancel.
 */
export function cancelUpload(id) {
  const item = items.find(i => i.id === id);
  if (!item) return;
  if (item.status === 'queued') { const pool = jobs.get(id)?.pool; dismissUpload(id); if (pool) pump(pool); return; }
  const up = handles.get(id);
  if (item.status === 'uploading' && up) { cancelled.add(id); up.cancel(); }
}

export function dismissUpload(id) { files.delete(id); assetIds.delete(id); jobs.delete(id); items = items.filter(i => i.id !== id); emit(); }
/** Close the sheet: drop every finished or failed file (nothing is cancelled; Close is disabled while one runs). */
export function clearUploads() {
  items = items.filter(i => i.status === 'queued' || i.status === 'uploading');
  for (const k of [...files.keys()]) if (!items.some(i => i.id === k)) { files.delete(k); assetIds.delete(k); jobs.delete(k); }
  emit();
}

const subscribe = cb => { itemListeners.add(cb); return () => itemListeners.delete(cb); };
const snapshot = () => items;

/** The queue, and the actions on it. */
export function useUploads() {
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  return { items: list, enqueueVideo: enqueueVideoUpload, enqueue: enqueueUpload, cancel: cancelUpload, retry: retryUpload, dismiss: dismissUpload, clear: clearUploads };
}

/** Call fn(item) whenever an upload finishes (item.result: the confirmed video asset, or the enqueueUpload confirm result). */
export function useUploadDone(fn) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const l = item => ref.current(item);
    doneListeners.add(l);
    return () => doneListeners.delete(l);
  }, []);
}

/** Renders the one Upload sheet for the shell it sits in, whenever the queue has files. */
export function UploadProvider({ children }) {
  const { items: list } = useUploads();
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useCallback(() => setCollapsed(c => !c), []);
  return (
    <>
      {children}
      {list.length > 0 && (
        <UploadSheet files={list} collapsed={collapsed} onToggle={toggle} onClose={clearUploads}
          onRetry={f => retryUpload(f.id)} onDismiss={f => dismissUpload(f.id)}
          onCancel={f => cancelUpload(f.id)} canCancel={f => f.status === 'queued' || handles.has(f.id)} />
      )}
    </>
  );
}
